import {
  Autocomplete,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import { isArray } from "lodash";
import { FC, SyntheticEvent, useEffect, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useDynamicFormsTranslate } from "@ryde/redux";
import {
  checkCleanupNestedValidation,
  dropdownsIsFetchingAtom,
  excludeNotArrayValue,
  ICustomField,
  Log,
  onlyNumericArrayValues,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { FullWidthTextFieldWrapper } from "../parts-styled";
import { useDropdownTemplate } from "./dropdowns";

export const TagsTemplate: FC<ICustomField> = ({
  id,
  name,
  formLabel,
  formAttributes,
  formValidation,
  e2eData = "tags-template",
}) => {
  const {
    requiredComputed,
    isDisabled,
    dropdownOptions,
    handleInsertTagItem,
    getOptionLabel,
  } = useDropdownTemplate("tags", name, formAttributes, formValidation);

  const { disableSorting } = formAttributes || {};

  const { t } = useTranslation();
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
  });

  const loadingItems = useRecoilValue(dropdownsIsFetchingAtom);
  const renderValue: number[] = getValues(name);
  useEffect(() => {
    if (renderValue && !isArray(renderValue)) {
      /* guard for invalid value coming from backend*/
      Log.warn(
        t("errors.multiSelectBoundTemplateInitialValueNotArray"),
        renderValue,
      );
      setValue(name, []);
    }
  }, [name, renderValue, setValue, t]);

  const onChangeDropdown = (
    e: SyntheticEvent<Element, Event>,
    newValue: number[],
  ): void => {
    Log.debug("Tags template value changed to:", newValue);
    setValue(name, newValue, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const {
    field: { value: formValue, ...restField },
    fieldState: { error },
  } = useController({ name, control });

  const sortedValue = useMemo(() => {
    const currentSelection = onlyNumericArrayValues(
      excludeNotArrayValue<string | number>(formValue),
    );
    if (disableSorting) {
      return currentSelection;
    }
    return currentSelection.toSorted((a, b) => {
      const labelA = getOptionLabel(a).toLowerCase();
      const labelB = getOptionLabel(b).toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, [formValue, disableSorting, getOptionLabel]);

  const value = useMemo(() => sortedValue, [sortedValue]);

  return (
    <FullWidthTextFieldWrapper>
      <FormControl
        data-e2e-template="tags-template"
        data-e2e-field-name={name}
        required={requiredComputed}
        error={!!error}
      >
        <Autocomplete
          {...restField}
          data-e2e={e2eData}
          data-e2e-id={`${name}_${id || ""}`}
          disabled={isDisabled || loadingItems}
          multiple
          freeSolo
          options={dropdownOptions || []}
          value={value}
          onChange={async (event, values) => {
            checkCleanupNestedValidation(name, errors);

            const lastValue = values[values.length - 1] as string;
            const isNewValue =
              lastValue && !dropdownOptions?.includes(lastValue);

            if (isNewValue) {
              Log.debug("New value entered:", lastValue);
              const saveTagResult = await handleInsertTagItem(lastValue);

              if (!saveTagResult.success) {
                warnAndToastSimpleResult(saveTagResult, t);
              }
            } else {
              onChangeDropdown(event, values as number[]);
            }
          }}
          isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
          getOptionLabel={getOptionLabel}
          renderInput={params => {
            return (
              <TextField
                {...params}
                label={formLabelTranslated}
                error={!!error}
                margin="normal"
                variant="outlined"
                required={requiredComputed}
                disabled={isDisabled || loadingItems}
                data-e2e="tags-template-input"
              />
            );
          }}
        />
        {error && (
          <FormHelperText data-e2e="tags-template-error">
            {error.message}
          </FormHelperText>
        )}
      </FormControl>
    </FullWidthTextFieldWrapper>
  );
};
